import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  NotionCandidateDTO,
  PartnerIntegrationKbDTO,
  PartnerKbPageDTO,
} from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// Internal-only "Knowledge Base" tab data. All endpoints live under
// /api/admin/integration-kb (rbac("internal")).

type CandidatesResponse = {
  configured: boolean;
  candidates: NotionCandidateDTO[];
};

// GET /api/admin/integration-kb/:partnerId — all linked pages, live-rendered.
export function usePartnerIntegrationKb(partnerId: string) {
  return useQuery<PartnerIntegrationKbDTO, ApiClientError>({
    queryKey: queryKeys.partners.integrationKb(partnerId),
    queryFn: () =>
      apiFetch<PartnerIntegrationKbDTO>(`/admin/integration-kb/${partnerId}`),
  });
}

// GET /api/admin/integration-kb/candidates?q= — link-picker source. Only fires
// while the picker is open.
export function useIntegrationKbCandidates(query: string | undefined, enabled: boolean) {
  return useQuery<CandidatesResponse, ApiClientError>({
    queryKey: queryKeys.partners.integrationKbCandidates(query),
    enabled,
    queryFn: () => {
      const qs = query ? `?q=${encodeURIComponent(query)}` : "";
      return apiFetch<CandidatesResponse>(`/admin/integration-kb/candidates${qs}`);
    },
  });
}

// Modeled as a mutation (not refetch) because it's an explicit user action with
// its own pending state. GET with ?refresh=1 re-pulls every page from Notion.
export function useRefreshPartnerIntegrationKb(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation<PartnerIntegrationKbDTO, ApiClientError, void>({
    mutationFn: () =>
      apiFetch<PartnerIntegrationKbDTO>(
        `/admin/integration-kb/${partnerId}?refresh=1`,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.partners.integrationKb(partnerId), data);
    },
    onError: (err: ApiClientError) => {
      toast.error(err.message ?? "Couldn't refresh from Notion");
    },
  });
}

// POST /api/admin/integration-kb/:partnerId/pages — link a page.
export function useAddPartnerKbPage(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation<PartnerIntegrationKbDTO, ApiClientError, { pageId: string; pageUrl?: string }>({
    mutationFn: (input: { pageId: string; pageUrl?: string }) =>
      apiFetch<PartnerIntegrationKbDTO>(
        `/admin/integration-kb/${partnerId}/pages`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.partners.integrationKb(partnerId), data);
    },
    onError: (err: ApiClientError) => {
      toast.error(
        err.status === 409
          ? "That page is already linked to this partner."
          : (err.message ?? "Couldn't link the page"),
      );
    },
  });
}

// DELETE /api/admin/integration-kb/:partnerId/pages/:linkId — unlink one page.
export function useRemovePartnerKbPage(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation<PartnerIntegrationKbDTO, ApiClientError, string>({
    mutationFn: (linkId: string) =>
      apiFetch<PartnerIntegrationKbDTO>(
        `/admin/integration-kb/${partnerId}/pages/${linkId}`,
        { method: "DELETE" },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.partners.integrationKb(partnerId), data);
    },
    onError: (err: ApiClientError) => {
      toast.error(err.message ?? "Couldn't unlink the page");
    },
  });
}

// PATCH /api/admin/integration-kb/:partnerId/pages/order — optimistic reorder.
// Mirrors use-reorder-faq-items: snapshot -> reorder in cache -> rollback on
// error -> onSettled refetch as the authoritative source.
export function useReorderPartnerKbPages(partnerId: string) {
  const queryClient = useQueryClient();
  const key = queryKeys.partners.integrationKb(partnerId);
  return useMutation<
    { ok: true },
    ApiClientError,
    string[],
    { previous: PartnerIntegrationKbDTO | undefined }
  >({
    mutationFn: (orderedLinkIds) =>
      apiFetch<{ ok: true }>(
        `/admin/integration-kb/${partnerId}/pages/order`,
        { method: "PATCH", body: JSON.stringify({ orderedLinkIds }) },
      ),
    onMutate: async (orderedLinkIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PartnerIntegrationKbDTO>(key);
      queryClient.setQueryData<PartnerIntegrationKbDTO>(key, (old) => {
        if (!old) return old;
        const byId = new Map<string, PartnerKbPageDTO>(
          old.pages.map((p) => [p.linkId, p]),
        );
        if (orderedLinkIds.some((id) => !byId.has(id))) return old;
        return { pages: orderedLinkIds.map((id) => byId.get(id)!) };
      });
      return { previous };
    },
    onError: (err, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
      toast.error(err.message ?? "Failed to reorder pages");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
